/**
 * How a tax rate reads to a buyer.
 *
 * Formatted in one place for the reason `formatCommissionRate` is: the rate
 * appears on the product page, in the order summary and on the receipt, and
 * three surfaces rounding the same number differently is the kind of thing a
 * buyer notices and nobody catches.
 *
 * Client-safe on purpose — no Prisma, no environment, no rate tables. The
 * number arrives on the `TaxEstimate` the server already sends.
 */

/**
 * Two decimals, with trailing zeros trimmed, so 9.5% is not "9.50%" and
 * Quebec's combined 14.975% does not round away to "14.98%" in a way that
 * makes the arithmetic on screen look wrong.
 *
 * Returns null when there is no rate worth showing. A zero rate means no tax
 * was charged, and the row is not drawn at all in that case.
 */
export function formatTaxRate(ratePercent: number | null | undefined): string | null {
  if (typeof ratePercent !== 'number' || !Number.isFinite(ratePercent) || ratePercent <= 0) return null;
  return `${Number(ratePercent.toFixed(3))}%`;
}

/**
 * The rate as it sits beside the label, parentheses included, or an empty
 * string when there is nothing to add. Callers render it as its own span after
 * the label, matching the processing-fee and commission rows.
 *
 * `approximate` is not decoration. Every quote the built-in table produces sets
 * it, because the local component of a state's rate is a state-wide average
 * rather than the buyer's own rooftop rate -- so in Colorado or Louisiana the
 * figure can be a couple of points either side of what that address really
 * owes. The flag reached the browser from the start and nothing rendered it,
 * which presented an average as an exact rate. It says "approx." now.
 */
export function taxRateSuffix(ratePercent: number | null | undefined, approximate?: boolean): string {
  const rate = formatTaxRate(ratePercent);
  if (!rate) return '';
  return approximate ? `(approx. ${rate})` : `(${rate})`;
}
